import { useEffect, useState } from 'react';
import CryptoJS from 'crypto-js';

type LsjImageStatus = 'idle' | 'loading' | 'ready' | 'error';

// 文档写的是 16 字符密钥字符串（AES-128），不是 hex 字节
const LSJ_AES_128_ECB_KEY = '525202f9149e061d';

function isBncUrl(src?: string | null): boolean {
  if (!src) return false;
  return /\.bnc(\?|$)/i.test(src);
}

function arrayBufferToWordArray(ab: ArrayBuffer): CryptoJS.lib.WordArray {
  const u8 = new Uint8Array(ab);
  const len = u8.length;
  const words: number[] = [];
  let i = 0;
  while (i < len) {
    const b0 = u8[i] ?? 0;
    const b1 = u8[i + 1] ?? 0;
    const b2 = u8[i + 2] ?? 0;
    const b3 = u8[i + 3] ?? 0;
    words.push((b0 << 24) | (b1 << 16) | (b2 << 8) | b3);
    i += 4;
  }
  return CryptoJS.lib.WordArray.create(words, len);
}

function wordArrayToUint8Array(wordArray: CryptoJS.lib.WordArray): Uint8Array {
  const { words, sigBytes } = wordArray;
  const out = new Uint8Array(sigBytes);
  let offset = 0;
  for (let i = 0; i < words.length; i++) {
    const w = words[i] | 0;
    const bytes = [(w >>> 24) & 0xff, (w >>> 16) & 0xff, (w >>> 8) & 0xff, w & 0xff];
    for (let j = 0; j < 4; j++) {
      if (offset >= sigBytes) break;
      out[offset++] = bytes[j];
    }
    if (offset >= sigBytes) break;
  }
  return out;
}

function detectImageMime(bytes: Uint8Array): string {
  if (!bytes || bytes.length < 12) return 'application/octet-stream';
  // PNG: 89 50 4E 47 0D 0A 1A 0A
  if (
    bytes[0] === 0x89 &&
    bytes[1] === 0x50 &&
    bytes[2] === 0x4e &&
    bytes[3] === 0x47 &&
    bytes[4] === 0x0d &&
    bytes[5] === 0x0a
  ) {
    return 'image/png';
  }
  // JPEG: FF D8 FF
  if (bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff) {
    return 'image/jpeg';
  }
  // GIF: GIF87a / GIF89a
  if (bytes[0] === 0x47 && bytes[1] === 0x49 && bytes[2] === 0x46) {
    return 'image/gif';
  }
  // BMP: BM
  if (bytes[0] === 0x42 && bytes[1] === 0x4d) {
    return 'image/bmp';
  }
  // WebP: RIFF....WEBP
  if (
    bytes[0] === 0x52 &&
    bytes[1] === 0x49 &&
    bytes[2] === 0x46 &&
    bytes[3] === 0x46 &&
    bytes[8] === 0x57 &&
    bytes[9] === 0x45 &&
    bytes[10] === 0x42 &&
    bytes[11] === 0x50
  ) {
    return 'image/webp';
  }
  return 'application/octet-stream';
}

// src(.bnc) -> decrypted object URL
const decryptedCache = new Map<string, string>();

export function useLsjBncImage(src?: string | null) {
  const [resolvedSrc, setResolvedSrc] = useState<string | null>(src ? (isBncUrl(src) ? null : src) : null);
  const [status, setStatus] = useState<LsjImageStatus>(src ? (isBncUrl(src) ? 'loading' : 'ready') : 'idle');

  useEffect(() => {
    let cancelled = false;

    if (!src) {
      setResolvedSrc(null);
      setStatus('idle');
      return () => undefined;
    }

    if (!isBncUrl(src)) {
      setResolvedSrc(src);
      setStatus('ready');
      return () => undefined;
    }

    const cached = decryptedCache.get(src);
    if (cached) {
      setResolvedSrc(cached);
      setStatus('ready');
      return () => undefined;
    }

    setResolvedSrc(null);
    setStatus('loading');

    void (async () => {
      try {
        const resp = await fetch(src, { cache: 'force-cache' });
        if (!resp.ok) throw new Error(`bnc fetch failed: ${resp.status}`);
        const ab = await resp.arrayBuffer();
        const cipherWA = arrayBufferToWordArray(ab);
        const keyWA = CryptoJS.enc.Utf8.parse(LSJ_AES_128_ECB_KEY);

        const cipherParams = CryptoJS.lib.CipherParams.create({
          ciphertext: cipherWA,
        } as any);
        const decrypted = CryptoJS.AES.decrypt(cipherParams, keyWA, {
          mode: CryptoJS.mode.ECB,
          padding: CryptoJS.pad.Pkcs7,
        });

        const plainU8 = wordArrayToUint8Array(decrypted);
        const mime = detectImageMime(plainU8);
        // TS 对 BlobPart 的类型收紧，这里显式拷贝成 ArrayBuffer 以便编译通过
        const buf = plainU8.buffer as ArrayBuffer;
        const blob = new Blob([buf.slice(plainU8.byteOffset, plainU8.byteOffset + plainU8.byteLength)], { type: mime });
        const objUrl = URL.createObjectURL(blob);

        if (cancelled) return;
        decryptedCache.set(src, objUrl);
        setResolvedSrc(objUrl);
        setStatus('ready');
      } catch {
        if (cancelled) return;
        setResolvedSrc(null);
        setStatus('error');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [src]);

  return { resolvedSrc, status } as const;
}

